import { useQuery } from '@tanstack/react-query'
import { Control, FieldPath, FieldValues } from 'react-hook-form'

import { fetchBridgeTokens } from '@/api/modules'
import GradientWrapper from '@/common/GradientWrapper'
import { Token } from '@/generated/proto/bridge/models/token'
import { queryKeys } from '@/queries/keys'
import {
  ControlledUiSelect,
  UiSelectContent,
  UiSelectItem,
  UiSelectTrigger,
  UiSelectValue,
} from '@/ui/UiSelect'

interface TokenSelectProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  disabled?: boolean
  placeholder?: string
  onTokenSelect?: (token: Token) => void
  onValueChange?: (tokenId: string) => void
}

export default function TokenSelect<TFieldValues extends FieldValues>({
  control,
  name,
  onValueChange,
  onTokenSelect,
  disabled = false,
  placeholder = 'Select a token',
}: TokenSelectProps<TFieldValues>) {
  const { data: tokens = [], isLoading: loadingTokens } = useQuery({
    queryKey: queryKeys.tokens.list(),
    queryFn: fetchBridgeTokens,
  })

  return (
    <ControlledUiSelect
      name={name}
      control={control}
      onValueChange={tokenId => {
        onValueChange?.(tokenId)
        const selectedToken = tokens.find(token => token.id === tokenId)
        if (selectedToken) {
          onTokenSelect?.(selectedToken)
        }
      }}
      disabled={disabled || loadingTokens}
    >
      <GradientWrapper>
        <UiSelectTrigger
          size='xl'
          className='bg-input border-border w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
        >
          <UiSelectValue
            placeholder={loadingTokens ? 'Loading...' : placeholder}
          />
        </UiSelectTrigger>
      </GradientWrapper>

      <UiSelectContent>
        {tokens.map(token => (
          <UiSelectItem key={token.id} value={token.id}>
            <div className='flex items-center gap-2'>
              {token.metadata?.uri && (
                <img
                  src={token.metadata.uri}
                  alt={token.metadata.name}
                  className='h-6 w-6 rounded'
                />
              )}
              {token.metadata?.name} ({token.metadata?.symbol})
            </div>
          </UiSelectItem>
        ))}
      </UiSelectContent>
    </ControlledUiSelect>
  )
}
